/* eslint-disable no-unused-expressions */

import React, { forwardRef, useCallback } from "react";
import { bool, node, string } from "prop-types";

import useToggleOnFocus from "../utilities/useToggleOnFocus";
import * as S from "./styled";

const Field = forwardRef(
  (
    {
      ariaDescribedBy,
      ariaLabelledBy,
      children,
      error,
      helper,
      hint,
      id,
      isValid,
      label,
      ...props
    },
    ref
  ) => {
    const helperID = `${id}-helperText`;
    const errorID = `${id}-errorText`;
    const labelID = `${id}-labelText`;
    const childComponent = React.Children.only(children);
    const childProps = children.props;

    const [isFocused, eventHandlers] = useToggleOnFocus();

    const focusHandler = useCallback(
      e => {
        eventHandlers.onFocus(e);
        childProps.onFocus?.(e);
      },
      [eventHandlers, childProps.onFocus]
    );

    const blurHandler = useCallback(
      e => {
        eventHandlers.onBlur(e);
        childProps.onBlur?.(e);
      },
      [eventHandlers, childProps.onBlur]
    );

    const hasError = !isValid && error;
    const describedByHelper = helper ? helperID : "";
    const describedByError = hasError ? errorID : "";
    const describedBy =
      `${describedByHelper} ${describedByError} ${ariaDescribedBy}`.trim() ||
      null;
    const labelledBy = `${labelID} ${ariaLabelledBy}`.trim();

    return (
      <S.Field ref={ref} {...props}>
        <S.Label hint={hint} htmlFor={id} id={labelID} isFocused={isFocused}>
          {label}
        </S.Label>
        {React.cloneElement(childComponent, {
          "aria-describedby": describedBy,
          "aria-labelledby": labelledBy,
          onBlur: blurHandler,
          onFocus: focusHandler,
          id,
          isValid
        })}
        {helper && (
          <S.HelperText id={helperID} isVisuallyHidden={!isValid}>
            {helper}
          </S.HelperText>
        )}
        {hasError && (
          <S.ErrorMessage
            aria-live="assertive"
            id={errorID}
            {...childProps.errorMessage}
          >
            {error}
          </S.ErrorMessage>
        )}
      </S.Field>
    );
  }
);

Field.displayName = "Field";

Field.propTypes = {
  ariaDescribedBy: string,
  ariaLabelledBy: string,
  children: node.isRequired,
  error: node,
  helper: node,
  hint: string,
  id: string.isRequired,
  isValid: bool,
  label: node.isRequired
};

Field.defaultProps = {
  ariaDescribedBy: "",
  ariaLabelledBy: "",
  isValid: true,
  error: null,
  hint: "",
  helper: null
};

export default Field;
